import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { InsightsTab } from '../types';
import useDiscoverSectionViewable from 'src/hooks/discover/useDiscoverSectionViewable';
import { useGetFeatureFlagsQuery } from 'src/services/dashboard-api';

export default function useTabs() {
  const { discoverAnalyticsEnabled, showInternalBadgeForAnalytics } =
    useDiscoverSectionViewable();

  const { data: featureFlagsData } = useGetFeatureFlagsQuery();

  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const isOverviewEnabled = featureFlags.includes('solve_insights_overview');

  const availableTabs = useMemo(() => {
    // Tab needs to be in the array in certain order
    const tabs = [];
    if (isOverviewEnabled) {
      tabs.push('Overview');
    }
    tabs.push('Conversations');
    tabs.push('Knowledge');
    tabs.push('Agents');
    if (discoverAnalyticsEnabled) {
      tabs.push(showInternalBadgeForAnalytics ? 'Topics - Internal' : 'Topics');
    }
    return tabs;
  }, [
    isOverviewEnabled,
    discoverAnalyticsEnabled,
    showInternalBadgeForAnalytics,
  ]);

  const tabTitleToIndex = useCallback(
    (tabTitle: string) =>
      availableTabs.findIndex(availableTab => availableTab === tabTitle),
    [availableTabs],
  );

  const tabIdToIndexMap = useMemo(
    () =>
      ({
        article: tabTitleToIndex('Knowledge'),
        chat: tabTitleToIndex('Conversations'),
        overview: tabTitleToIndex('Overview'),
        topic: tabTitleToIndex('Topics'),
        workflow: tabTitleToIndex('Agents'),
      }) as const satisfies Record<InsightsTab, number>,
    [tabTitleToIndex],
  );

  const [searchParams] = useSearchParams();
  const serializedTab = searchParams.get('tab') ?? '0';
  const activeTab = Number(serializedTab);

  const currentInsightPage =
    availableTabs[activeTab] === 'Agents'
      ? 'workflow'
      : availableTabs[activeTab] === 'Overview'
        ? 'overview'
        : availableTabs[activeTab] === 'Knowledge'
          ? 'knowledge'
          : 'conversation';

  return {
    availableTabs,
    currentInsightPage,
    tabIdToIndexMap,
  } as const;
}
